import { canUnitAttack } from '../combat/resolveCombat.js';
import { enumerateTargetCandidates, enumerateTargetSelections, targetCandidateKey, validateAndCanonicalizeTargetSelection } from './enumerate.js';
import { buildPublicTargetContext } from './publicContext.js';
function legacyTargetKind(definition, previous) {
    const mode = previous.flatMap((step) => step.selection).find((candidate) => candidate.kind === 'mode');
    if (mode?.kind === 'mode')
        return definition.planning?.targetByMode?.[mode.id] ?? definition.planning?.target ?? 'none';
    return definition.planning?.target ?? 'none';
}
function legacySpec(kind, definition) {
    if (kind === 'unit') {
        return {
            kind: 'unit',
            cardinality: { min: 1, max: 1 },
            ordered: false,
            ...(definition.planning?.unitFilter !== undefined ? { side: definition.planning.unitFilter } : {}),
        };
    }
    return { kind: 'cell', cardinality: { min: 1, max: 1 }, ordered: false };
}
function assertSteps(steps) {
    const ids = steps.map((step) => step.stepId);
    if (ids.some((id) => typeof id !== 'string' || id === '') || new Set(ids).size !== ids.length) {
        throw new Error('planning steps требуют уникальные непустые stepId');
    }
    if (steps.some((step) => step.onInvalidAtResolution !== undefined
        && step.onInvalidAtResolution !== 'all_or_nothing'
        && step.onInvalidAtResolution !== 'allow_partial')) {
        throw new Error('planning step содержит неизвестную reveal policy');
    }
}
/** Resolves the complete ordered step list for a definition and prior selections. */
export function planningStepsForDefinition(definition, context, previous = []) {
    const planning = definition.planning;
    if (planning?.steps !== undefined) {
        const steps = typeof planning.steps === 'function' ? planning.steps(context, previous) : planning.steps;
        assertSteps(steps);
        return steps;
    }
    const steps = [];
    if (planning?.modes !== undefined) {
        steps.push({
            stepId: 'mode',
            spec: {
                kind: 'mode',
                cardinality: { min: 1, max: 1 },
                ordered: false,
                options: planning.modes.map((id) => ({ id })),
            },
        });
    }
    const target = legacyTargetKind(definition, previous);
    if (typeof target === 'object') {
        steps.push({ stepId: 'target', spec: target });
    }
    else if (target === 'unit') {
        steps.push({ stepId: 'unit', spec: legacySpec('unit', definition) });
    }
    else if (target === 'cell') {
        steps.push({ stepId: 'cell', spec: legacySpec('cell', definition) });
    }
    else if (target === 'unit+cell') {
        steps.push({ stepId: 'unit', spec: legacySpec('unit', definition) });
        steps.push({ stepId: 'cell', spec: legacySpec('cell', definition) });
    }
    assertSteps(steps);
    return steps;
}
export function implicitPlanningSteps(definition, context, previous = []) {
    if (definition.type === 'UNIT' || definition.type === 'STRUCTURE') {
        if (definition.planning?.steps !== undefined) {
            const steps = planningStepsForDefinition(definition, context, previous);
            if (context.phase !== 'planning' || typeof definition.planning.fieldPlacement === 'object') {
                return steps;
            }
            // Ordinary FIELD destinations are soft intents while planning: the
            // occupant (or another staged FIELD card) may leave before this exact
            // queue position resolves. Keep every authored restriction except the
            // transient occupancy snapshot; reveal-time revalidation runs in the
            // resolution phase and therefore restores the authored empty-cell gate.
            return steps.map((step) => step.stepId === 'placement' && step.spec.kind === 'cell'
                ? { ...step, spec: { ...step.spec, occupancy: 'any' } }
                : step);
        }
        return [{
                stepId: 'placement',
                spec: {
                    kind: 'cell',
                    side: 'ally',
                    occupancy: context.phase === 'planning' ? 'any' : 'empty',
                    cardinality: { min: 1, max: 1 },
                },
            }];
    }
    if (definition.type === 'REACTION')
        return [];
    return planningStepsForDefinition(definition, context, previous);
}
export function legacyPlanCardSelections(action, definition) {
    if (action.selections !== undefined)
        return action.selections.map((step) => ({
            stepId: step.stepId, selection: structuredClone(step.selection),
        }));
    if (definition.type === 'UNIT' || definition.type === 'STRUCTURE') {
        return action.cellId === undefined ? [] : [{
                stepId: 'placement', selection: [{ kind: 'cell', cellId: action.cellId }],
            }];
    }
    const selections = [];
    if (action.mode !== undefined)
        selections.push({ stepId: 'mode', selection: [{ kind: 'mode', id: action.mode }] });
    if (action.targetInstanceId !== undefined) {
        selections.push({ stepId: 'unit', selection: [{ kind: 'unit', instanceId: action.targetInstanceId }] });
    }
    if (action.targetCellId !== undefined) {
        selections.push({ stepId: 'cell', selection: [{ kind: 'cell', cellId: action.targetCellId }] });
    }
    return selections;
}
export function planSelections(plan) {
    if (plan.selections !== undefined)
        return plan.selections.map((step) => ({
            stepId: step.stepId, selection: structuredClone(step.selection),
        }));
    const selections = [];
    if ('mode' in plan && plan.mode !== undefined) {
        selections.push({ stepId: 'mode', selection: [{ kind: 'mode', id: plan.mode }] });
    }
    if ('targetInstanceId' in plan && plan.targetInstanceId !== undefined) {
        selections.push({ stepId: 'unit', selection: [{ kind: 'unit', instanceId: plan.targetInstanceId }] });
    }
    if ('targetCellId' in plan && plan.targetCellId !== undefined) {
        selections.push({ stepId: 'cell', selection: [{ kind: 'cell', cellId: plan.targetCellId }] });
    }
    if ((plan.kind === undefined || plan.kind === 'FIELD') && plan.cellId !== undefined) {
        selections.push({ stepId: 'placement', selection: [{ kind: 'cell', cellId: plan.cellId }] });
    }
    return selections;
}
function selectedCandidate(selections, kind) {
    return selections.flatMap((step) => step.selection).find((candidate) => candidate.kind === kind);
}
/** Transient compatibility view for old hooks; no scalar fields are persisted by new actions. */
export function withLegacyPlanProjection(plan) {
    if (plan.selections === undefined)
        return structuredClone(plan);
    const projected = structuredClone(plan);
    const mode = selectedCandidate(plan.selections, 'mode');
    const unit = selectedCandidate(plan.selections, 'unit');
    const card = selectedCandidate(plan.selections, 'card');
    const cell = plan.selections
        .filter((step) => step.stepId !== 'placement')
        .flatMap((step) => step.selection)
        .find((candidate) => candidate.kind === 'cell');
    if (mode?.kind === 'mode')
        projected.mode = mode.id;
    if (unit?.kind === 'unit')
        projected.targetInstanceId = unit.instanceId;
    else if (card?.kind === 'card')
        projected.targetInstanceId = card.instanceId;
    if (cell?.kind === 'cell')
        projected.targetCellId = cell.cellId;
    return projected;
}
function validateLegacyExtras(state, definition, playerId, instanceId, selections, registry) {
    const projected = withLegacyPlanProjection({
        kind: 'SPELL', instanceId, planSeq: 0, selections,
    });
    if (definition.planning?.unitMustBeAttackEligible === true
        && projected.targetInstanceId !== undefined
        && !canUnitAttack(state, registry, projected.targetInstanceId))
        return false;
    return definition.planning?.validatePlan?.(state, playerId, projected) ?? true;
}
/** Authoritative committed validator. It never suspends resolution. */
export function validateCommittedPlanningSelections(state, registry, playerId, instanceId, definition, submitted) {
    const context = buildPublicTargetContext(state, playerId, registry);
    const canonical = [];
    for (let index = 0; index < submitted.length; index += 1) {
        const step = implicitPlanningSteps(definition, context, canonical)[index];
        const selection = submitted[index];
        if (step === undefined || selection === undefined || selection.stepId !== step.stepId) {
            throw new Error(`planning: неожиданный step "${selection?.stepId ?? '<missing>'}" на позиции ${index}`);
        }
        const domain = enumerateTargetCandidates(step.spec, context, canonical);
        canonical.push({
            stepId: step.stepId,
            selection: validateAndCanonicalizeTargetSelection(step.spec, domain, selection.selection),
        });
    }
    const expected = implicitPlanningSteps(definition, context, canonical);
    if (canonical.length !== expected.length) {
        throw new Error(`planning: передано ${canonical.length} steps, требуется ${expected.length}`);
    }
    if (!validateLegacyExtras(state, definition, playerId, instanceId, canonical, registry)) {
        throw new Error('planning: legacy validatePlan отклонил выбор');
    }
    return { selections: canonical, context };
}
/** Reveal-time validator; allow_partial passes only candidates still in the live domain. */
export function revalidatePlannedTargets(state, registry, playerId, definition, plan) {
    const context = buildPublicTargetContext(state, playerId, registry);
    const original = planSelections(plan);
    const next = [];
    let valid = true;
    for (let index = 0; index < original.length; index += 1) {
        const step = implicitPlanningSteps(definition, context, next)[index];
        const selection = original[index];
        if (step === undefined || selection === undefined || selection.stepId !== step.stepId) {
            valid = false;
            continue;
        }
        const domain = enumerateTargetCandidates(step.spec, context, next);
        const liveKeys = new Set(domain.map(targetCandidateKey));
        const retained = selection.selection.filter((candidate) => liveKeys.has(targetCandidateKey(candidate)));
        const stepIsValid = retained.length === selection.selection.length
            && retained.length >= step.spec.cardinality.min
            && retained.length <= step.spec.cardinality.max;
        if (!stepIsValid && step.onInvalidAtResolution !== 'allow_partial')
            valid = false;
        next.push({ stepId: step.stepId, selection: step.spec.ordered === true ? retained : retained.sort((a, b) => domain.findIndex((candidate) => targetCandidateKey(candidate) === targetCandidateKey(a))
                - domain.findIndex((candidate) => targetCandidateKey(candidate) === targetCandidateKey(b))),
        });
    }
    const finalExpected = implicitPlanningSteps(definition, context, next);
    if (finalExpected.length !== original.length
        || finalExpected.some((step, index) => original[index]?.stepId !== step.stepId)) {
        valid = false;
    }
    if (!validateLegacyExtras(state, definition, playerId, plan.instanceId, next, registry))
        valid = false;
    const policy = definition.planning?.onInvalidAtResolution
        ?? (definition.planning?.allowPartialFizzle === true ? 'allow_partial' : 'all_or_nothing');
    const resolvedSelections = valid || policy === 'allow_partial' ? next : original;
    return {
        valid,
        plan: withLegacyPlanProjection({ ...structuredClone(plan), selections: resolvedSelections }),
    };
}
/**
 * Internal candidate layer for the public legal-action enumerator. It only
 * expands the actor-visible target domain; the actions layer owns every
 * authoritative gate, including card-owned legacy validation.
 */
export function enumeratePlanningActionCandidates(state, registry, playerId, instanceId) {
    const instance = state.instances[instanceId];
    const definition = instance === undefined ? undefined : registry.get(instance.defId);
    if (definition === undefined)
        return [];
    const context = buildPublicTargetContext(state, playerId, registry);
    const out = [];
    const visit = (previous) => {
        const steps = implicitPlanningSteps(definition, context, previous);
        const step = steps[previous.length];
        if (step === undefined) {
            if (previous.length === steps.length) {
                out.push({ kind: 'PlanCard', playerId, instanceId, selections: structuredClone(previous) });
            }
            return;
        }
        const domain = enumerateTargetCandidates(step.spec, context, previous);
        for (const selection of enumerateTargetSelections(step.spec, domain)) {
            visit([...previous, { stepId: step.stepId, selection }]);
        }
    };
    visit([]);
    return out;
}
export function placementCellFromSelections(selection) {
    const candidate = selection.find((step) => step.stepId === 'placement')?.selection[0];
    return candidate?.kind === 'cell' ? candidate.cellId : undefined;
}
export function targetInstanceFromSelections(selection) {
    const candidate = selection.flatMap((step) => step.selection)
        .find((item) => item.kind === 'unit' || item.kind === 'card');
    return candidate?.kind === 'unit' || candidate?.kind === 'card' ? candidate.instanceId : undefined;
}
/** Exact single UNIT selected by a named committed planning step. */
export function unitInstanceFromStep(selections, stepId) {
    const selection = selections.find((step) => step.stepId === stepId)?.selection;
    if (selection?.length !== 1)
        return undefined;
    const candidate = selection[0];
    return candidate?.kind === 'unit' ? candidate.instanceId : undefined;
}
//# sourceMappingURL=planning.js.map